import { FC } from 'react';
import { createUseStyles } from 'react-jss';
import { IonText, isPlatform, useIonToast } from '@ionic/react';
import { AttachmentList } from './AttachmentList.tsx';
import { FileRejection, useDropzone } from 'react-dropzone';
import { useInstance } from '../support/ioc/useInstance';
import { useSubscription } from '@autopylot-internal/state';
import { FileUploader, IUploadingFile } from './FileUploader.ts';
import { Button } from '../components/button/Button.tsx';
import { PaperclipIcon, UploadIcon } from '../components/icons/Icons.tsx';
import { IAttachment } from './NotesClient.ts';
import { FieldLabel } from '../components/field/FieldLabel.tsx';

const maxFileSizeMB = 50;
// this is the actual file size we validate against
// with some padding, so that a ${maxFileSizeMB}MB file on disk, that is say ${maxFileSizeMB}.01 MB in memory
// when we validate it, doesn't throw
const maxFileSize = (maxFileSizeMB + .5) * 1024 * 1024;

const isDesktop = isPlatform('desktop');

const useStyles = createUseStyles({
    attachmentArea: isDesktop ? {
        margin: '-10px 20px',
        minHeight: 170,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        '& hr': { margin: '-12px 0 12px' }
    } : {
        margin: '-10px 20px',
        minHeight: 70,
        paddingBottom: 6,
        '& hr': { margin: '-12px 0 12px' }
    },

    addAttachments: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',

        '& > div': {
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        }
    },

    dropZone: {
        padding: 2,
        borderRadius: 5,
        border: '2px dashed transparent'
    },

    dragAccept: {
        padding: 2,
        borderRadius: 5,
        border: '2px dashed var(--ion-color-success)'
    },

    browseLink: {
        display: 'flex',
        cursor: 'pointer',
        color: 'var(--ion-color-primary)'
    }
});

export interface IAttachmentAreaProps {
    attachments: IAttachment[];
    onUpload: (uploadedFiles: IAttachment[]) => void;
    onDelete: (attachment: IAttachment) => void;
}

export const AttachmentArea: FC<IAttachmentAreaProps> = ({ attachments: existingFiles, onUpload, onDelete }) => {
    const classes = useStyles();

    const [showToast] = useIonToast();

    const uploader = useInstance(FileUploader);
    useSubscription(() => uploader);

    const onDrop = async (files: File[]) => {
        const uploadedFiles = await uploader.upload(files);
        onUpload(uploadedFiles);
    };

    const onRejected = (rejections: FileRejection[]) => {
        const files = rejections.map(r => r.file.name);
        const message = `Unable to attach ${files.join(', ')}. Files must be under ${maxFileSizeMB} MB.`;
        showToast({ message, duration: 5000 });
    };

    const {getRootProps, getInputProps, open, isDragAccept} = useDropzone({
        noClick: true,
        noKeyboard: true,
        maxSize: maxFileSize,
        onDropAccepted: onDrop,
        onDropRejected: onRejected
    });

    const uploadingAttachments: IUploadingFile[] = uploader.uploadingFiles.filter(f => !f.isUploaded);

    const attachments: IAttachment[] = [
        ...existingFiles,
        // and files that are uploaded, but haven't been saved to the note yet,
        // because we are waiting for the rest to finish
        ...uploader.uploadingFiles.filter(f => f.isUploaded).map((f) => ({
            fileName: f.fileName,

            // we know we have these (!) since isUploaded === true
            contentType: f.contentType!,
            url: f.url!
        }))
    ];

    const hasAttachmentsOrUploading = attachments.length || uploadingAttachments.length;

    const attachmentList = (
        <AttachmentList
            attachments={attachments}
            uploadingAttachments={uploadingAttachments}
            onDelete={onDelete}
        />
    );

    return (
        <div className={classes.attachmentArea}>
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                <FieldLabel>Attachments</FieldLabel>
                <Button fill="clear" onClick={open}><PaperclipIcon />&nbsp;Add</Button>
            </div>

            <hr />

            <div
                data-id="add-attachments"
                style={isDesktop ? {marginBottom: 6} : {display: 'none'}}
                className={!hasAttachmentsOrUploading && isDesktop ? classes.addAttachments : undefined}
            >
                <div {...getRootProps({className: isDragAccept ? classes.dragAccept : classes.dropZone})}>
                    <input {...getInputProps()} data-type="dropzone" />

                    {!hasAttachmentsOrUploading && (
                        <>
                            <div className={classes.browseLink} style={{display: 'inline-block'}} onClick={open}>
                                <UploadIcon size="medium" />
                            </div>
                            <div style={{marginLeft: 4, display: 'inline-block'}}>
                                Drag and drop or <div className={classes.browseLink} style={{display: 'inline-block'}} onClick={open}>Add</div>
                            </div>
                        </>
                    )}

                    {attachmentList}
                </div>
            </div>

            {/* on mobile, since the add-attachments area is hidden, show the attachment list */}
            {!isDesktop && (
                <>
                    {!hasAttachmentsOrUploading && (
                        <IonText color="medium">No photos or documents added</IonText>
                    )}

                    {attachmentList}
                </>
            )}
        </div>
    );
};
